import { useState, type ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    DndContext,
    KeyboardSensor,
    MouseSensor,
    TouchSensor,
    closestCenter,
    useSensor,
    useSensors,
    type DragEndEvent
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SearchField from './SearchField';

/** One column on offer; `visible` ones are ticked at the start and on reset. */
export interface ColumnChoice {
    field: string;
    label: string;
    visible: boolean;
}

/** The picker's words: the grid's own column panel texts where they fit. */
export interface ColumnPickerTexts {
    search: string;
    noColumns: string;
    reset: string;
}

/**
 * Columns to export, laid out like the grid's own column panel — search on
 * top, a scrolling list, "all" and reset below. That panel itself cannot be
 * reused: it switches the grid's visibility, not a selection of its own.
 * Reset goes back to the columns marked `visible`, or does `onReset`.
 *
 * With `onReorder` each column has a grip to drag it by, the keyboard too
 * (space, arrows, space); the list shows the columns in the order given. Not
 * while searching: a move within a filtered list lands in an unseen place.
 * A column in `disabledFields` keeps its tick and its place but cannot be
 * switched.
 */
export default function ColumnPicker({
    columns,
    selectedFields,
    onChange,
    onReorder,
    onReset,
    disabledFields,
    texts: gridText
}: {
    columns: readonly ColumnChoice[];
    selectedFields: Set<string>;
    onChange: (fields: Set<string>) => void;
    onReorder?: (fields: string[]) => void;
    onReset?: () => void;
    disabledFields?: ReadonlySet<string>;
    texts: ColumnPickerTexts;
}) {
    const intl = useIntl();
    const [search, setSearch] = useState('');
    const sensors = useSensors(
        useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
        // A short hold on a tablet, so a swipe still scrolls the list.
        useSensor(TouchSensor, {
            activationConstraint: { delay: 200, tolerance: 6 }
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates
        })
    );
    const term = search.trim().toLowerCase();
    const shownColumns = term
        ? columns.filter((column) => column.label.toLowerCase().includes(term))
        : columns;
    const switchable = shownColumns.filter(
        (column) => !disabledFields?.has(column.field)
    );
    const shownSelected = switchable.filter((column) =>
        selectedFields.has(column.field)
    ).length;
    const allShownSelected =
        switchable.length > 0 && shownSelected === switchable.length;
    const sortable = Boolean(onReorder) && !term;

    function toggleField(field: string) {
        const next = new Set(selectedFields);
        if (next.has(field)) {
            next.delete(field);
        } else {
            next.add(field);
        }
        onChange(next);
    }

    // While searching, only the columns found: "Datum" + all = every date.
    function toggleAllShown() {
        const next = new Set(selectedFields);
        for (const column of switchable) {
            if (allShownSelected) {
                next.delete(column.field);
            } else {
                next.add(column.field);
            }
        }
        onChange(next);
    }

    function moveColumn({ active, over }: DragEndEvent) {
        if (!onReorder || !over || active.id === over.id) {
            return;
        }
        const fields = columns.map((column) => column.field);
        onReorder(
            arrayMove(
                fields,
                fields.indexOf(String(active.id)),
                fields.indexOf(String(over.id))
            )
        );
    }

    function reset() {
        if (onReset) {
            onReset();
            return;
        }
        onChange(
            new Set(
                columns
                    .filter((column) => column.visible)
                    .map((column) => column.field)
            )
        );
    }

    return (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Box sx={{ p: 1 }}>
                <SearchField
                    fullWidth
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    onClear={() => setSearch('')}
                    placeholder={gridText.search}
                    aria-label={gridText.search}
                />
            </Box>
            {/* Scrolls on its own, so a grid with many columns keeps the
                row choice and the buttons in view. */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    px: 1.5,
                    height: 280,
                    overflowY: 'auto'
                }}
            >
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={moveColumn}
                >
                    <SortableContext
                        items={shownColumns.map((column) => column.field)}
                        strategy={verticalListSortingStrategy}
                    >
                        {shownColumns.map((column) => (
                            <ColumnRow
                                key={column.field}
                                field={column.field}
                                sortable={sortable}
                                moveLabel={intl.formatMessage(
                                    { id: 'grid.csvExport.columns.move' },
                                    { column: column.label }
                                )}
                            >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={selectedFields.has(
                                                column.field
                                            )}
                                            disabled={disabledFields?.has(
                                                column.field
                                            )}
                                            onChange={() =>
                                                toggleField(column.field)
                                            }
                                        />
                                    }
                                    label={column.label}
                                />
                            </ColumnRow>
                        ))}
                    </SortableContext>
                </DndContext>
                {shownColumns.length === 0 && (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ py: 1 }}
                    >
                        {gridText.noColumns}
                    </Typography>
                )}
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 1.5,
                    py: 0.5,
                    borderTop: 1,
                    borderColor: 'divider'
                }}
            >
                <FormControlLabel
                    control={
                        <Checkbox
                            size="small"
                            checked={allShownSelected}
                            indeterminate={
                                !allShownSelected && shownSelected > 0
                            }
                            disabled={switchable.length === 0}
                            onChange={toggleAllShown}
                        />
                    }
                    label={<FormattedMessage id="grid.csvExport.columns.all" />}
                />
                <Button size="small" onClick={reset}>
                    {gridText.reset}
                </Button>
            </Box>
        </Box>
    );
}

/** One column of the list; when `sortable`, a grip up front to drag it by. */
function ColumnRow({
    field,
    sortable,
    moveLabel,
    children
}: {
    field: string;
    sortable: boolean;
    moveLabel: string;
    children: ReactNode;
}) {
    const {
        attributes,
        listeners,
        setNodeRef,
        setActivatorNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: field, disabled: !sortable });
    return (
        <Box
            ref={setNodeRef}
            sx={{
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                zIndex: isDragging ? 1 : undefined,
                bgcolor: isDragging ? 'action.hover' : undefined,
                borderRadius: 1
            }}
            style={{
                transform: CSS.Translate.toString(transform),
                transition
            }}
        >
            {sortable ? (
                <Box
                    ref={setActivatorNodeRef}
                    {...attributes}
                    {...listeners}
                    aria-label={moveLabel}
                    sx={{
                        display: 'flex',
                        color: 'text.secondary',
                        cursor: isDragging ? 'grabbing' : 'grab',
                        touchAction: 'none',
                        mr: 0.5,
                        borderRadius: 0.5,
                        '&:focus-visible': {
                            outline: '2px solid',
                            outlineColor: 'primary.main'
                        }
                    }}
                >
                    <DragIndicatorIcon fontSize="small" />
                </Box>
            ) : null}
            {children}
        </Box>
    );
}
